import { ShieldCheck, ShieldAlert, ShieldQuestion, Clock, ExternalLink } from "lucide-react";
import {
  certBadgeState,
  CERT_BADGE_COPY,
  type CertBadgeState,
} from "@/lib/grading/cert-verification";

const STYLES: Record<
  CertBadgeState,
  { wrap: string; icon: typeof ShieldCheck; iconClass: string; title: string; body: string }
> = {
  officially_verified: {
    wrap: "border-emerald-500/30 bg-emerald-500/5",
    icon: ShieldCheck,
    iconClass: "text-emerald-400",
    title: "text-emerald-200",
    body: "text-emerald-200/80",
  },
  verification_pending: {
    wrap: "border-sky-500/30 bg-sky-500/5",
    icon: Clock,
    iconClass: "text-sky-400",
    title: "text-sky-100",
    body: "text-sky-200/80",
  },
  manual_official_check: {
    wrap: "border-amber-500/30 bg-amber-500/5",
    icon: ShieldQuestion,
    iconClass: "text-amber-400",
    title: "text-amber-100",
    body: "text-amber-200/80",
  },
  not_independently_verified: {
    wrap: "border-amber-500/30 bg-amber-500/5",
    icon: ShieldAlert,
    iconClass: "text-amber-400",
    title: "text-amber-100",
    body: "text-amber-200/80",
  },
};

/**
 * Buyer-facing grading certificate badge. Renders exactly ONE state:
 * Officially Verified · Verification Pending · Manual Official Check ·
 * Not Independently Verified.
 */
export function GradingCertBadge({
  company,
  certNumber,
  verified,
  status,
  officialUrl,
  verifiedAt,
}: {
  company: string | null;
  certNumber: string | null;
  verified: boolean | null;
  status?: string | null;
  officialUrl?: string | null;
  verifiedAt?: string | null;
}) {
  const state = certBadgeState({ certNumber, verified, status });
  if (!state || !certNumber) return null;

  const s = STYLES[state];
  const copy = CERT_BADGE_COPY[state];
  const Icon = s.icon;
  const label = (company ?? "").toUpperCase();

  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${s.wrap}`}
      role="status"
      aria-live="polite"
      data-cert-state={state}
    >
      <Icon className={`h-4 w-4 shrink-0 ${s.iconClass}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${s.title}`}>{copy.title}</div>
        <p className={`text-xs ${s.body}`}>
          {copy.detail} {label ? `${label} cert ` : "Cert "}
          <span className="font-mono">{certNumber}</span>
          {state === "officially_verified" && verifiedAt
            ? ` · ${new Date(verifiedAt).toLocaleDateString()}`
            : ""}
        </p>
      </div>
      {officialUrl && (
        <a
          href={officialUrl}
          target="_blank"
          rel="noreferrer noopener"
          className={`inline-flex min-h-11 items-center gap-1 text-xs font-medium underline underline-offset-4 ${s.title}`}
        >
          {state === "officially_verified" ? "Official source" : "Official lookup"}
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      )}
    </div>
  );
}
